/**
 * Shared FIFO concurrency limiter for Zemax analysis POSTs.
 *
 * Each worker process executes analyses serially (one OpticStudio connection
 * behind a lock), so N viewers fetching at once — a desktop-mode surface of up
 * to MAX_WINDOWS viewers, or a refresh-all after an optimize — just pile up in
 * the bridge queue, and a large burst can approach the backend's per-bridge
 * pending cap (429 BRIDGE_OVERLOADED). Funneling every analysis POST through
 * this limiter keeps at most MAX_CONCURRENT requests in flight while
 * preserving submission order for the rest.
 *
 * Concurrency 2 (not 1): keeps the pipeline full — one request computing on
 * the worker while the next is already queued bridge-side — without letting a
 * mass refresh monopolize the bridge.
 */
const MAX_CONCURRENT = 2;

/**
 * A slot is LEASED, not owned: after SLOT_LEASE_MS the slot is released even
 * if the request is still in flight (the request itself is NOT aborted).
 * Rationale: legitimate analyses can run for minutes on the serial worker, so
 * killing them on a timeout would regress previously-working viewers — but a
 * hung bridge socket (bounded only by ZEMAX_PROXY_TIMEOUT_MS, ~10 min) must
 * not starve every other viewer of one of the two global slots. Under
 * pathological hangs, concurrency degrades toward the pre-limiter unlimited
 * behavior instead of deadlocking the app.
 */
const SLOT_LEASE_MS = 90_000;

let active = 0;
const waiters: Array<() => void> = [];

// A freed slot is TRANSFERRED to the next waiter without ever decrementing
// `active` during the handoff — decrement-then-reincrement would open a
// microtask window where a fresh caller sees active < MAX_CONCURRENT and
// enters alongside the woken waiter, exceeding the cap and jumping the FIFO.
function passOn() {
  const next = waiters.shift();
  if (next) next();
  else active--;
}

/**
 * `signal` is checked after the slot is acquired: a request aborted while
 * waiting in the queue must not hit the network at all. Throws the signal's
 * abort reason (a DOMException named AbortError).
 */
export async function withZemaxFetchSlot<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  if (active >= MAX_CONCURRENT) {
    // The slot is handed over already-counted — do not increment on wake.
    await new Promise<void>((resolve) => waiters.push(resolve));
  } else {
    active++;
  }
  if (signal?.aborted) {
    passOn();
    // Not signal.throwIfAborted(): absent on pre-2022 engines, where the
    // TypeError would surface as a real viewer error instead of a silent
    // supersede. `?? new DOMException(...)` covers `reason` being absent too.
    throw signal.reason ?? new DOMException('Aborted while queued for a Zemax fetch slot', 'AbortError');
  }
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    passOn();
  };
  const lease = setTimeout(release, SLOT_LEASE_MS);
  try {
    return await task();
  } finally {
    clearTimeout(lease);
    release();
  }
}
